import type { Exercise, Measurement, SyncOperation, WorkoutPlan, WorkoutSession } from './types';
import { homeProgramExercises, homeProgramPlans } from './homeProgram';

const NAME = 'tempo-local';
const VERSION = 2;
export const uid = () => crypto.randomUUID();

export const starterExercises: Exercise[] = [
  { id: 'catalog-squat', name: 'Barbell Squat', muscleGroup: 'Legs', equipment: 'Barbell', exerciseType: 'strength', isCustom: false },
  { id: 'catalog-bench', name: 'Bench Press', muscleGroup: 'Chest', equipment: 'Barbell', exerciseType: 'strength', isCustom: false },
  { id: 'catalog-row', name: 'Seated Cable Row', muscleGroup: 'Back', equipment: 'Cable', exerciseType: 'strength', isCustom: false },
  { id: 'catalog-pushup', name: 'Push-up', muscleGroup: 'Chest', equipment: 'Bodyweight', exerciseType: 'strength', isCustom: false },
  { id: 'catalog-rdl', name: 'Romanian Deadlift', muscleGroup: 'Legs', equipment: 'Barbell', exerciseType: 'strength', isCustom: false },
  { id: 'catalog-press', name: 'Overhead Press', muscleGroup: 'Shoulders', equipment: 'Barbell', exerciseType: 'strength', isCustom: false },
  ...homeProgramExercises,
];

function open() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      ['sessions', 'measurements', 'queue', 'plans', 'exercises'].forEach((store) => {
        if (!db.objectStoreNames.contains(store)) db.createObjectStore(store, { keyPath: 'id' });
      });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function put<T extends { id: string }>(store: string, value: T) {
  const db = await open();
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(store, 'readwrite');
    transaction.objectStore(store).put(value);
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

async function all<T>(store: string): Promise<T[]> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store);
    const request = tx.objectStore(store).getAll();
    tx.oncomplete = () => db.close();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function remove(store: string, id: string) {
  const db = await open();
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(store, 'readwrite');
    transaction.objectStore(store).delete(id);
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

export const local = {
  sessions: () => all<WorkoutSession>('sessions'),
  measurements: () => all<Measurement>('measurements'),
  plans: () => all<WorkoutPlan>('plans'),
  customExercises: () => all<Exercise>('exercises'),
  queue: () => all<SyncOperation>('queue'),
  saveSession: (session: WorkoutSession) => put('sessions', session),
  saveMeasurement: (measurement: Measurement) => put('measurements', measurement),
  savePlan: (plan: WorkoutPlan) => put('plans', plan),
  saveExercise: (exercise: Exercise) => put('exercises', exercise),
  deletePlan: (id: string) => remove('plans', id),
  enqueue: (operation: SyncOperation) => put('queue', operation),
  updateOperation: (operation: SyncOperation) => put('queue', operation),
};

// One transaction keeps the local record and its upload operation together.
// New edits replace only pending operations, preventing unbounded per-keystroke queues.
export async function saveRecord(store: 'sessions' | 'measurements' | 'plans' | 'exercises', value: SyncOperation['payload'], type: SyncOperation['type']) {
  const db = await open();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction([store, 'queue'], 'readwrite');
    tx.objectStore(store).put(value);
    const queue = tx.objectStore('queue');
    const request = queue.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        const previous = cursor.value as SyncOperation;
        if (previous.entityId === value.id && previous.type === type && previous.state !== 'synced') cursor.delete();
        cursor.continue();
      } else queue.put({ id: uid(), entityId: value.id, type, payload: value, createdAt: new Date().toISOString(), state: 'pending' });
    };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onabort = tx.onerror = () => { db.close(); reject(tx.error ?? new Error('Penyimpanan gagal.')); };
  });
}

export async function restoreRecords(backup: { sessions: WorkoutSession[]; measurements: Measurement[]; plans: WorkoutPlan[]; customExercises: Exercise[] }) {
  const db = await open();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(['sessions','measurements','plans','exercises'], 'readwrite');
    for (const [name, values] of Object.entries({ sessions: backup.sessions, measurements: backup.measurements, plans: backup.plans, exercises: backup.customExercises })) {
      const store = tx.objectStore(name);
      for (const value of values) {
        const request = store.get(value.id);
        request.onsuccess = () => { if (!request.result) store.put(value); };
      }
    }
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onabort = tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function seedHomeProgram(): Promise<void> {
  const seedKey = 'tempo-home-program-v1';
  if (localStorage.getItem(seedKey)) return;
  const existing = await local.plans();
  const existingIds = new Set(existing.map((plan) => plan.id));
  await Promise.all(homeProgramPlans.filter((plan) => !existingIds.has(plan.id)).map((plan) => local.savePlan(plan)));
  localStorage.setItem(seedKey, new Date().toISOString());
}
